// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { adjustment, AdjustmentDO, sensor, SensorListDO } from '../../../requests';
import { typeSensorVariable, TypeSensorVariableListDO } from '../../../../configuration/requests';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

interface Props {
  rowId?: number;
  notify: () => void;
}

interface State {
  loading: boolean;
  sensorArray: SensorListDO[];
  typeVariableArray: TypeSensorVariableListDO[];

  data: Partial<AdjustmentDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private defaultValues: any;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,
      sensorArray: [],
      typeVariableArray: [],

      data: {
        comments: '',
        formula: '',
      }
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    this.loadingStart();
    try {
      const sensorArray: SensorListDO[] = await sensor<SensorListDO[]>('list') || [];

      let adjustmentRow: AdjustmentDO | undefined;
      let typeVariableArray: TypeSensorVariableListDO[] = [];
      if (rowId !== undefined && !isNaN(rowId)) {
        adjustmentRow = await adjustment<AdjustmentDO>('get', String(rowId));
        typeVariableArray = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId: adjustmentRow?.sensor.typeSensor.id }) || [];
      }

      if (this.mounted) {
        this.setState(
          {
            data: { ...adjustmentRow, },
            sensorArray,
            typeVariableArray,
          },
          () => {
            this.defaultValues = _.cloneDeep(adjustmentRow);
          }
        );
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;
    if (!this.isValidForm(data)) return;

    this.loadingStart();
    adjustment('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Adjustment successfully ${data.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('AD'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public isValidForm = (data: Partial<AdjustmentDO>): boolean => {
    const sensorId: number | undefined = data?.sensor?.id;
    if (!sensorId) {
      messageError('Error', 'Please select a device');
      return false;
    }

    const typeSensorVariableId: number | undefined = data?.typeSensorVariable?.id;
    if (!typeSensorVariableId) {
      messageError('Error', 'Please select a variable');
      return false;
    }
    return true;
  };

  public handleChangeStandard = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement |HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const splitName: string[] = name.split('.');
    if (splitName.length === 2) {
      const [outerName, innerName] = splitName;
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            [innerName]: value,
          },
        },
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: value,
        },
      }));
    }
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const { sensorArray } = this.state;
    const [outerName, innerName] = name.split('.');
    const { typeSensorId } = sensorArray.find((item: SensorListDO) => item.id === +value) || {};

    // Only peform update if the value changes
    if (String(value) !== String(this.state.data?.[outerName]?.[innerName])) {
      typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId })
        ?.then((resp: TypeSensorVariableListDO[]) => {
          this.setState((prevState: State) => ({
            data: {
              ...prevState.data,
              [outerName]: {
                [innerName]: value,
              },
              typeSensorVariable: undefined,
            },
            typeVariableArray: resp,
          }));
        }).catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        });
    }
  };

  public render(): JSX.Element {
    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={this.state.loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Settings {this.state.data.id ? `Edit: ${this.state.data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChangeDevice={this.handleChangeDevice}
                        onChangeStandard={this.handleChangeStandard}
                      />
                      <ButtonsForm formValues={this.state} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
